import { apiClient } from '../core/apiClient';

/**
 * Interfaz para iniciar una sesión de caja
 */
export interface IStartCashSessionData {
  cajeroId: string;
  efectivoDeclarado: number;
  posId: number;
}

/**
 * Interfaz para la respuesta de iniciar sesión
 */
export interface IStartCashSessionResponse {
  success: boolean;
  message: string;
  data: {
    sesion: {
      id: string;
      cajeroId: string;
      inicioTurno: string;
      finTurno: string | null;
      efectivoDeclarado: number;
      efectivoCalculadoSistema: number;
      estado: 'ABIERTA' | 'CERRADA';
    };
    cajero: {
      id: string;
      nombre: string;
      apellido: string;
      rut: string;
    };
  };
}

/**
 * Interfaz para la respuesta de cerrar sesión
 */
export interface ICloseCashSessionResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    cajero_id: string;
    inicio_turno: string;
    fin_turno: string;
    efectivo_declarado: string;
    efectivo_calculado_sistema: string;
    estado: 'CERRADA';
  };
}

/**
 * Interfaz para datos de movimiento de caja
 */
export interface ICashMovementData {
  tipo: 'INGRESO' | 'RETIRO' | 'AJUSTE';
  monto: number;
  descripcion: string;
  cajeroId: string;
}

/**
 * Interfaz para la respuesta de movimiento de caja
 */
export interface ICashMovementResponse {
  success: boolean;
  message: string;
  data: {
    movimiento: {
      id: string;
      tipo: string;
      monto: number;
      descripcion: string;
    };
    sesion: {
      id: string;
      cajeroId: string;
      efectivoDeclarado: number;
      efectivoCalculado: number;
      efectivoCalculadoSistema: number;
      efectivoCalculadoNuevo: number;
      estado: 'ABIERTA';
    };
    cajero: {
      id: string;
      nombre: string;
      apellido: string;
      email: string;
    };
  };
}

/**
 * Interfaz para el estado de la sesión de caja
 */
export interface ICashSessionStatus {
  isOpen: boolean;
  sessionId: string | null;
  cajeroId: string | null;
  efectivoDeclarado: number;
  efectivoCalculado: number;
  inicioTurno: string | null;
}

/**
 * Interfaz para sesión activa del backend
 */
export interface IActiveSessionResponse {
  success: boolean;
  message: string;
  data: {
    hasActiveSession: boolean;
    activeSession?: {
      id: string;
      cajeroId: string;
      userId: string; // Alias para cajeroId
      posId: number;
      posName?: string;
      efectivoDeclarado: number;
      startTime: string; // Equivale a inicioTurno
      inicioTurno: string; // Para compatibilidad
      status: 'ACTIVE' | 'ABIERTA';
      estado: 'ACTIVE' | 'ABIERTA'; // Para compatibilidad
      efectivoCalculadoSistema: number;
      currentBalance?: number; // Equivale a efectivoCalculadoSistema
      timeSinceStart?: string;
      horasAbiertas: number;
      lastActivity?: string;
      pos?: {
        id: number;
        nombre: string;
      };
    };
  };
}

/**
 * Interfaz para forzar cierre de sesión
 */
export interface IForceCloseSessionResponse {
  success: boolean;
  message: string;
  data: {
    closedSession: {
      id: string;
      userId: string;
      posId: number;
      startTime: string;
      endTime: string;
      duration: string;
      finalBalance: number;
      status: 'FORCE_CLOSED' | 'CERRADA';
    };
  };
}

/**
 * Servicio para la gestión de sesiones de caja
 */
class CashSessionService {
  private static readonly STORAGE_KEY = 'cash_session';

  /**
   * Inicia una nueva sesión de caja
   */
  static async startSession(data: IStartCashSessionData): Promise<IStartCashSessionResponse> {
    try {
      const response = await apiClient.post<IStartCashSessionResponse>('/api/caja/turno/iniciar', data);
      
      // Guardar la sesión en localStorage
      this.saveSessionToStorage(response.data.data.sesion);
      
      return response.data;
    } catch (error) {
      console.error('Error iniciando sesión de caja:', error);
      throw error;
    }
  }

  /**
   * Cierra la sesión de caja actual
   */
  static async closeSession(sessionId: string): Promise<ICloseCashSessionResponse> {
    try {
      const response = await apiClient.put<ICloseCashSessionResponse>(`/api/caja/turno/${sessionId}/finalizar`);
      
      // Limpiar la sesión del localStorage
      this.clearSessionFromStorage();
      
      return response.data;
    } catch (error) {
      console.error('Error cerrando sesión de caja:', error);
      throw error;
    }
  }

  /**
   * Registra un movimiento en la sesión de caja
   */
  static async registerMovement(sessionId: string, movementData: ICashMovementData): Promise<ICashMovementResponse> {
    try {
      const response = await apiClient.post<ICashMovementResponse>(
        `/api/caja/turno/${sessionId}/movimiento`,
        movementData
      );
      
      // Actualizar el efectivo calculado en el storage
      if (response.data.data.sesion) {
        this.updateSessionInStorage({
          efectivoCalculado: response.data.data.sesion.efectivoCalculadoNuevo
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error registrando movimiento de caja:', error);
      throw error;
    }
  }

  /**
   * Obtiene el estado actual de la sesión de caja
   */
  static getCurrentSession(): ICashSessionStatus {
    try {
      const sessionData = localStorage.getItem(this.STORAGE_KEY);
      if (!sessionData) {
        return {
          isOpen: false,
          sessionId: null,
          cajeroId: null,
          efectivoDeclarado: 0,
          efectivoCalculado: 0,
          inicioTurno: null
        };
      }

      const session = JSON.parse(sessionData);
      return {
        isOpen: session.estado === 'ABIERTA',
        sessionId: session.id,
        cajeroId: session.cajeroId,
        efectivoDeclarado: session.efectivoDeclarado,
        efectivoCalculado: session.efectivoCalculado || session.efectivoDeclarado,
        inicioTurno: session.inicioTurno
      };
    } catch (error) {
      console.error('Error obteniendo sesión actual:', error);
      return {
        isOpen: false,
        sessionId: null,
        cajeroId: null,
        efectivoDeclarado: 0,
        efectivoCalculado: 0,
        inicioTurno: null
      };
    }
  }

  /**
   * Verifica si hay una sesión abierta
   */
  static hasOpenSession(): boolean {
    const session = this.getCurrentSession();
    return session.isOpen && session.sessionId !== null;
  }

  /**
   * Obtiene el ID de la sesión actual
   */
  static getCurrentSessionId(): string | null {
    const session = this.getCurrentSession();
    return session.isOpen ? session.sessionId : null;
  }

  /**
   * Guarda la sesión en localStorage
   */
  private static saveSessionToStorage(sessionData: any): void {
    try {
      const sessionToSave = {
        ...sessionData,
        efectivoCalculado: sessionData.efectivoCalculadoSistema || sessionData.efectivoDeclarado
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessionToSave));
    } catch (error) {
      console.error('Error guardando sesión en storage:', error);
    }
  }

  /**
   * Actualiza datos específicos de la sesión en localStorage
   */
  private static updateSessionInStorage(updates: Partial<{ efectivoCalculado: number }>): void {
    try {
      const sessionData = localStorage.getItem(this.STORAGE_KEY);
      if (sessionData) {
        const session = JSON.parse(sessionData);
        const updatedSession = { ...session, ...updates };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSession));
      }
    } catch (error) {
      console.error('Error actualizando sesión en storage:', error);
    }
  }

  /**
   * Limpia la sesión del localStorage
   */
  private static clearSessionFromStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error limpiando sesión del storage:', error);
    }
  }

  /**
   * Valida si el cajero actual puede iniciar una sesión
   */
  static validateCashierForSession(cajeroId: string): boolean {
    const currentSession = this.getCurrentSession();
    
    // Si no hay sesión abierta, cualquier cajero puede iniciar
    if (!currentSession.isOpen) {
      return true;
    }
    
    // Si hay sesión abierta, solo el mismo cajero puede trabajar
    return currentSession.cajeroId === cajeroId;
  }

  /**
   * Obtiene información resumida de la sesión para mostrar en UI
   */
  static getSessionSummary(): {
    isOpen: boolean;
    sessionId: string | null;
    efectivoInicial: number;
    efectivoActual: number;
    diferencia: number;
    horasAbiertas: number;
  } {
    const session = this.getCurrentSession();
    
    if (!session.isOpen) {
      return {
        isOpen: false,
        sessionId: null,
        efectivoInicial: 0,
        efectivoActual: 0,
        diferencia: 0,
        horasAbiertas: 0
      };
    }

    const horasAbiertas = session.inicioTurno 
      ? (new Date().getTime() - new Date(session.inicioTurno).getTime()) / (1000 * 60 * 60)
      : 0;

    return {
      isOpen: true,
      sessionId: session.sessionId,
      efectivoInicial: session.efectivoDeclarado,
      efectivoActual: session.efectivoCalculado,
      diferencia: session.efectivoCalculado - session.efectivoDeclarado,
      horasAbiertas: Math.round(horasAbiertas * 100) / 100
    };
  }

  /**
   * Verifica si el usuario tiene una sesión activa en el backend
   */
  static async checkActiveSession(cajeroId: string): Promise<IActiveSessionResponse> {
    try {
      const response = await apiClient.get<IActiveSessionResponse>(`/api/caja/cash-sessions/active/${cajeroId}`);
      return response.data;
    } catch (error) {
      console.error('Error verificando sesión activa:', error);
      throw error;
    }
  }

  /**
   * Recupera una sesión activa del backend y la sincroniza con localStorage
   */
  static async recoverActiveSession(sessionId: string): Promise<ICashSessionStatus> {
    try {
      const response = await apiClient.post(`/api/caja/cash-sessions/recover/${sessionId}`, {
        confirmRecovery: true
      });
      
      if (response.data.success && response.data.session) {
        // Sincronizar con localStorage usando la estructura de respuesta
        const sessionData = {
          id: response.data.session.id,
          cajeroId: response.data.session.userId,
          inicioTurno: response.data.session.startTime,
          efectivoDeclarado: response.data.session.efectivoDeclarado,
          efectivoCalculadoSistema: response.data.session.currentBalance || response.data.session.efectivoDeclarado,
          estado: 'ABIERTA'
        };
        
        this.saveSessionToStorage(sessionData);
        
        // Retornar el estado actualizado
        return this.getCurrentSession();
      }
      
      throw new Error('No se pudo recuperar la sesión');
    } catch (error) {
      console.error('Error recuperando sesión activa:', error);
      throw error;
    }
  }

  /**
   * Fuerza el cierre de una sesión activa en el backend
   */
  static async forceCloseActiveSession(sessionId: string): Promise<IForceCloseSessionResponse> {
    try {
      // Obtener información de la sesión actual para el cierre
      const currentSession = this.getCurrentSession();
      
      const response = await apiClient.post<IForceCloseSessionResponse>(`/api/caja/cash-sessions/force-close/${sessionId}`, {
        userId: currentSession.cajeroId,
        reason: 'FORCE_CLOSE_NEW_SESSION',
        closingBalance: currentSession.efectivoCalculado,
        notes: 'Sesión cerrada automáticamente para abrir nueva sesión'
      });
      
      // Limpiar localStorage si es la sesión actual
      if (currentSession.sessionId === sessionId) {
        this.clearSessionFromStorage();
      }
      
      return response.data;
    } catch (error) {
      console.error('Error forzando cierre de sesión:', error);
      throw error;
    }
  }

  /**
   * Sincroniza el estado local con el backend
   */
  static async syncWithBackend(): Promise<boolean> {
    try {
      const localSession = this.getCurrentSession();
      
      if (!localSession.isOpen || !localSession.sessionId) {
        return true; // No hay nada que sincronizar
      }

      // Verificar si la sesión sigue activa en el backend
      const response = await apiClient.get(`/api/caja/turno/${localSession.sessionId}`);
      
      if (response.data.success && response.data.data.sesion) {
        const backendSession = response.data.data.sesion;
        
        // Si la sesión está cerrada en el backend, limpiar localStorage
        if (backendSession.estado === 'CERRADA') {
          this.clearSessionFromStorage();
          return false;
        }
        
        // Actualizar datos locales con los del backend
        this.saveSessionToStorage(backendSession);
        return true;
      }
      
      // Si no se encontró la sesión en el backend, limpiar localStorage
      this.clearSessionFromStorage();
      return false;
    } catch (error) {
      console.error('Error sincronizando con backend:', error);
      // En caso de error de red, mantener el estado local
      return true;
    }
  }
}

export default CashSessionService;
